<template>
  <div class="portal-accounts">
    <BasePreloader v-if="carrierImportLoading && !carrierImportLoaded" color="purple" size="l"/>

    <template v-else>
      <p class="portal-accounts__title">
        {{ $t('carriers.import.tab.PortalAccounts') }} ({{ accounts.length }})
      </p>
      <p class="portal-accounts__description">{{ $t('carriers.import.tab.Description') }}</p>

      <p v-if="!accounts.length" class="portal-accounts__empty">
        {{ $t('carriers.import.tab.Empty') }}
      </p>

      <div
        v-for="account in accounts"
        :key="account.scrapeAccountId"
        class="portal-accounts__row"
      >
        <div class="portal-accounts__main">
          <p class="portal-accounts__label">
            {{ account.label || account.username }}
            <span class="portal-accounts__state" :class="`portal-accounts__state--${stateOf(account)}`">
              {{ $t(`carriers.import.state.${stateOf(account)}`) }}
            </span>
            <span v-if="account.trustPortal" class="portal-accounts__state portal-accounts__state--trusted">
              {{ $t('carriers.import.trust.Pill') }}
            </span>
          </p>
          <p class="portal-accounts__sub">{{ subtitleOf(account) }}</p>
          <p class="portal-accounts__last">{{ lastLineOf(account) }}</p>
          <!-- The login's schedule, set in the same window as the carrier row's pill. -->
          <button
            v-if="schedulable(account)"
            type="button"
            class="portal-accounts__pill"
            :class="{ 'portal-accounts__pill--set': scheduleOf(account) }"
            @click="scheduling = account"
          >
            <template v-if="scheduleOf(account)">
              <span class="portal-accounts__pill-icon">⟳</span>
              <b>{{ scheduleOf(account) }}</b>
            </template>
            <template v-else>
              <span class="portal-accounts__pill-icon">+</span>
              <b>{{ $t('carriers.import.cell.SetSchedule') }}</b>
              <span class="portal-accounts__pill-hint">· {{ $t('carriers.import.cell.OnlyWhenRead') }}</span>
            </template>
          </button>
          <p v-else class="portal-accounts__last">{{ $t('carriers.import.daily.Blocked.notSupported') }}</p>
        </div>
        <div class="portal-accounts__actions">
          <!-- The portal turned the password down: the one thing this login needs, first. -->
          <BaseButton
            v-if="stateOf(account) === 'needsLogin' && canUpdatePassword"
            is-small
            @click="changingPassword = account"
          >{{ $t('carriers.import.password.Update') }}</BaseButton>
          <BaseButton is-small type="outline" @click="editing = account">
            {{ $t('carriers.import.tab.Settings') }}
          </BaseButton>
          <BaseButton is-small type="outline" @click="viewingHistory = account">
            {{ $t('carriers.import.tab.ImportHistory') }}
          </BaseButton>
        </div>
      </div>

      <BaseButton
        is-small
        type="outline"
        class="portal-accounts__add"
        @click="editing = 'new'"
      >
        {{ $t('carriers.import.tab.ConnectNewAccount') }}
      </BaseButton>
    </template>

    <PortalAccountModal
      v-if="editing"
      :insurance-company-id="insuranceCompanyId"
      :carrier-name="carrierName"
      :carrier-code="carrierCode"
      :account="editing === 'new' ? null : editing"
      @saved="reload"
      @close="editing = null"
    />

    <PortalAccountHistoryModal
      v-if="viewingHistory"
      :account="viewingHistory"
      @close="viewingHistory = null"
    />

    <UpdatePasswordDialog
      v-if="changingPassword"
      :account="changingPassword"
      @close="changingPassword = null"
    />

    <CarrierScheduleModal
      v-if="scheduling"
      :carrier-name="carrierName"
      :accounts="[scheduling]"
      @saved="reload"
      @close="scheduling = null"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import { UserPermissions } from '@/utils/permissions';
import {
  accountState, latestJobFor, runWhen, scheduleSummary, stoppedPartWay,
} from '@/utils/carrierImport';
import CarrierScheduleModal from './CarrierScheduleModal';
import PortalAccountModal from './PortalAccountModal';
import PortalAccountHistoryModal from './PortalAccountHistoryModal';
import UpdatePasswordDialog from './UpdatePasswordDialog';

/**
 * The "Portal accounts" tab of the Edit Carrier modal: which logins Renova has
 * for this carrier's portal. Nothing is edited here — each row opens its own
 * window, because a login is a username, a password, a mailbox and a history,
 * and squeezing several of those into one list is what made the old screen
 * unreadable.
 */
export default {
  name: 'PortalAccountsTab',
  components: {
    BaseButton, BasePreloader, CarrierScheduleModal, PortalAccountModal, PortalAccountHistoryModal, UpdatePasswordDialog,
  },
  mixins: [UserPermissions],
  props: {
    insuranceCompanyId: { type: String, required: true },
    carrierName: { type: String, default: '' },
    /** The carrier the record is saved as, if any: a new login opens its portal. */
    carrierCode: { type: String, default: null },
    /** Open this account's window as soon as the tab mounts. */
    initialAccountId: { type: String, default: null },
  },
  data() {
    return {
      editing: null, viewingHistory: null, scheduling: null, changingPassword: null,
    };
  },
  computed: {
    ...mapGetters([
      'carrierImportLoading', 'carrierImportLoaded', 'carrierImportAccountsByCompany',
      'carrierImportRuns', 'carrierImportPortals',
    ]),
    accounts() {
      return this.carrierImportAccountsByCompany[this.insuranceCompanyId] || [];
    },
    /** Changing a password is a policy write, like starting a read (as in ScheduledToday). */
    canUpdatePassword() {
      return this.checkUserPermissions([this.PermissionsTypes.POLICIES_UPDATE]);
    },
  },
  created() {
    Promise.all([
      this.carrierImportLoaded
        ? Promise.resolve()
        : this.$store.dispatch('carrierImportFetchAccounts'),
      this.$store.dispatch('carrierImportFetchRuns'),
    ]).then(() => {
      if (!this.initialAccountId) return;
      const found = this.accounts.find((a) => a.scrapeAccountId === this.initialAccountId);
      if (found) this.editing = found;
    });
  },
  methods: {
    /** Portal and user; an API-key portal (Handle) has no user worth showing. */
    subtitleOf(account) {
      const portal = (this.carrierImportPortals || []).find((p) => p.code === account.portal);
      if (portal && portal.credentialKind === 'API_KEY') return account.portalName;
      return `${account.portalName} · ${account.username}`;
    },
    stateOf(account) {
      return accountState(account, latestJobFor(this.carrierImportRuns.items, account.scrapeAccountId));
    },
    /** The polled run when there is one (fresher while a read goes), else the login's own. */
    lastLineOf(account) {
      const run = latestJobFor(this.carrierImportRuns.items, account.scrapeAccountId) || account.lastRun;
      if (!run) return this.$t('carriers.import.cell.NeverImported');
      const when = runWhen(run.finishedAt || run.updatedAt || run.createdAt, this.$i18n.locale, this.$t.bind(this));
      const line = this.$t('carriers.import.cell.LastImport', { when });
      return stoppedPartWay(run) ? `${line} · ${this.$t('carriers.import.cell.StoppedPartWay')}` : line;
    },
    schedulable(account) {
      const portal = (this.carrierImportPortals || []).find((p) => p.code === account.portal);
      return Boolean(portal && (portal.schedulableKinds || []).length);
    },
    /** "Daily · 06:30 · payments and policies", or null when it reads only on Read. */
    scheduleOf(account) {
      const summary = scheduleSummary(account.schedule, this.$t.bind(this), this.$i18n.locale);
      if (!summary) return null;
      return `${summary} · ${this.$t(`carriers.import.schedule.reads.${account.schedule.kinds}`)}`;
    },
    reload() {
      this.$store.dispatch('carrierImportFetchAccounts', true);
      this.$store.dispatch('carrierImportFetchRuns');
    },
  },
};
</script>

<style lang="scss" scoped>
.portal-accounts {
  &__title {
    font-size: 16px;
    font-weight: 600;
    color: #23233c;
    margin-bottom: 4px;
  }

  &__description,
  &__empty {
    font-size: 13px;
    color: #8f8f9f;
    margin-bottom: 16px;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border: 1px solid #ededf2;
    border-radius: 8px;
    margin-bottom: 8px;
  }

  &__main { flex: 1; min-width: 0; }

  // Wraps under the account on a narrow modal rather than squeezing the label.
  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    flex: none;
  }

  &__label {
    font-size: 14px;
    font-weight: 600;
    color: #23233c;
  }

  &__sub {
    font-size: 12px;
    color: #8f8f9f;
    margin-top: 2px;
  }

  &__last {
    font-size: 12px;
    color: #8f8f9f;
    margin-top: 6px;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    padding: 4px 12px;
    border: 1px solid var(--solid-lavender-grey);
    border-radius: 20px;
    background: transparent;
    font-size: 12.5px;
    color: var(--solid-dark-purple);
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;

    b { font-weight: 600; }

    &:hover {
      background: var(--solid-purple-02);
      border-color: var(--solid-purple);
      color: var(--solid-purple);
    }

    &--set {
      border-color: var(--solid-purple-07);
      color: var(--solid-purple);
    }
  }

  &__pill-icon { font-weight: 600; }

  &__pill-hint { opacity: 0.8; }

  &__state {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 9px;
    border-radius: 100px;
    font-size: 11px;
    font-weight: 500;
    background: #f2f2f7;
    color: #6b6b85;

    &--connected { background: #e7f9f0; color: #14a35c; }
    &--running { background: #eeebfd; color: #6f5ce6; }
    &--needsCode,
    &--needsLogin,
    &--needsSetup,
    &--locked,
    &--failed { background: #fff2e5; color: #d97a17; }
    &--trusted { background: #eeebfd; color: #4b3bb8; }
  }

  &__add { margin-top: 8px; }
}
</style>
